/**
 * PageTransition — route-change choreography via GSAP.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { gsap } from '@/lib/gsap';

export function PageTransition(): JSX.Element {
  const location = useLocation();
  const outlet = useOutlet();
  const [displayed, setDisplayed] = useState({ outlet, pathname: location.pathname });
  const overlayRef = useRef<HTMLDivElement>(null);
  const wipeRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (location.pathname === displayed.pathname) return;

    // Skip the glitch wipe between study routes — players switching from
    // PUZZLE → TYPING → SPEAKING want to keep momentum, the wipe felt jarring.
    const insideStudy = location.pathname.startsWith('/app/study/') && displayed.pathname.startsWith('/app/study/');
    if (insideStudy) {
      setDisplayed({ outlet, pathname: location.pathname });
      return;
    }

    const overlay = overlayRef.current;
    const wipe = wipeRef.current;
    const content = contentRef.current;
    if (!overlay || !wipe || !content) {
      setDisplayed({ outlet, pathname: location.pathname });
      return;
    }

    const tl = gsap.timeline({
      onComplete: () => { setDisplayed({ outlet, pathname: location.pathname }); },
    });

    tl.set(overlay, { display: 'block' });
    tl.fromTo(content, { opacity: 1, filter: 'blur(0px)' }, {
      opacity: 0.4, filter: 'blur(6px) saturate(1.6)', duration: 0.22, ease: 'power2.in',
    }, 0);
    tl.fromTo(wipe, { scaleX: 0, transformOrigin: '0% 50%' }, {
      scaleX: 1, duration: 0.24, ease: 'power3.out',
    }, 0);

    return () => { tl.kill(); };
  }, [location.pathname, displayed.pathname, outlet]);

  useLayoutEffect(() => {
    const overlay = overlayRef.current;
    const wipe = wipeRef.current;
    const content = contentRef.current;
    if (!overlay || !wipe || !content) return;

    const tl = gsap.timeline();
    tl.fromTo(content, { opacity: 0.2, filter: 'blur(8px)' }, {
      opacity: 1, filter: 'blur(0px)', duration: 0.45, ease: 'expo.out',
    });
    tl.to(wipe, { scaleX: 0, transformOrigin: '100% 50%', duration: 0.32, ease: 'power3.in' }, 0);
    tl.set(overlay, { display: 'none' });

    return () => { tl.kill(); };
  }, [displayed.pathname]);

  return (
    <>
      <div ref={contentRef}>{displayed.outlet}</div>
      <div
        ref={overlayRef}
        className="pointer-events-none fixed inset-0 z-[55]"
        style={{ display: 'none' }}
      >
        <div
          ref={wipeRef}
          className="absolute inset-0"
          style={{
            transform: 'scaleX(0)',
            background:
              'linear-gradient(90deg, transparent 0%, rgba(0,240,255,0.18) 30%, rgba(255,46,136,0.22) 70%, transparent 100%)',
            backgroundSize: '200% 100%',
          }}
        >
          <div className="absolute inset-0 mix-blend-overlay" style={{
            backgroundImage:
              'repeating-linear-gradient(to bottom, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 3px)',
          }} />
        </div>
      </div>
    </>
  );
}
